import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, Select, MenuItem, Menu, IconButton, ListItem, ListItemButton, ListItemText, useTheme, Button, Tooltip, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useAuthStore from '../../../store/authStore';
import { CONSTANTS } from '../../../constants';
import { getKeyByValue } from '../../../utils';
import { addUserRoleApi, deleteUser, getOrganizationsApi } from '../../../api';
import useTranslations from '../../../hooks/useTranslation';
import IconContainer from '../../../components/common/IconContainer';
import { exitIcon, singlePersonIcon, changePasswordIcon, searchIcon, switchAccountIcon, deletesIcon } from '../../../utils/svg';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { superAdminExitMasqueradeApi, superAdminMasqueradeRoleSwitchApi } from '../../../api';

import { useLocation, useNavigate } from 'react-router-dom';
import useAdminStore from '../../../store/adminStore';
import useLeaderStore from '../../../store/leaderStore';
import Modal from '../../../components/common/Modal';
import useAdminMobileRestriction from '../../../hooks/useAdminMobileRestriction';

const DashboardHeader = ({ heading, handleAddClick, isSearch, searchHandler, isAddingUser, isAddingGroup, isAddingDisciple, isAddingOrganisation, isAddingCampaign, isAddingDraftDay }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const iconColor = theme.palette.icon.primary;
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useTranslations();
  const { user, loginNewUser, setOrganization, setTheme, org_to_theme, selectedOrganization, logoutUser } = useAuthStore(state => state);
  const [anchorEl, setAnchorEl] = useState(null);
  const rolesArray = typeof user?.user_permissions === 'string' ? user.user_permissions.split(',') : (Array.isArray(user?.user_permissions) ? user.user_permissions : []);

  const userRole = rolesArray?.map(role => {
    const roles = getKeyByValue(CONSTANTS.USER_ROLES, role);
    return {
      label: roles,
      value: roles
    };
  });
  const { clearAdminData } = useAdminStore(state => state);
  const { clearLeaderData } = useLeaderStore(state => state);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user?.user_role || '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const isSuperAdminMasquerading = useAuthStore(state => state.isSuperAdminMasquerading);
  const setSuperAdminMasquerading = useAuthStore(state => state.setSuperAdminMasquerading);

  const isAdmin = rolesArray?.includes('1') && !rolesArray?.includes('0');
  const isSuperAdmin = rolesArray?.includes('0');
  // Show only on these screens for SuperAdmin
  const allowedSuperAdminPaths = ['/super-admin/all-users'];
  const isAllowedForSuperAdmin = allowedSuperAdminPaths.includes(location.pathname);
  const rawOrgs = isAdmin ? (orgs.length > 0 ? orgs : user?.organizations || []) : orgs;
  const roleFilteredOrgIds = new Set(
    user?.user_organizations
      ?.filter(uo => {
        if (['DELETED', 'INVITED', 'MOVED', 'Moved'].includes(uo.status)) return false;
        if (!user?.user_role || isSuperAdmin) return true;
        const currentRole = (user.user_role || '').toUpperCase();
        const uoRole = (uo.user_role || '').toUpperCase();
        const isRoleMatch = (currentRole === 'ADMINISTRATOR' || currentRole === 'ADMIN')
          ? (uoRole === 'ADMIN' || uoRole === 'ADMINISTRATOR')
          : uoRole === currentRole;
        if (!isRoleMatch) return false;
        return true;
      })
      ?.map(uo => String(uo.organization_id)) || []
  );
  const organizations = rawOrgs?.filter(org => roleFilteredOrgIds.size === 0 || roleFilteredOrgIds.has(String(org.id)));
  const isDiscipleView = user.is_impersonate;
  const { isAdminMobileRestricted } = useAdminMobileRestriction();
  const fetchOrgs = async (userId = '') => {
    try {
      // setLoading(true);
      const response = await getOrganizationsApi(userId);
      if (response?.data?.status === 200) {
        const fetchedOrgs = response?.data?.data?.updatedOrganizations || response?.data?.data || [];
        setOrgs(fetchedOrgs);
        const validOrgs = fetchedOrgs;
        if (!selectedOrganization && validOrgs?.length > 0) {
          const defaultOrganization = validOrgs.find(org => org?.deactivate_login !== 1 && !['MOVED', 'ARCHIVED', 'DELETED'].includes((org?.user_org_status || '').toUpperCase())) || validOrgs.find(org => org?.deactivate_login !== 1) || validOrgs[0];
          setOrganization(defaultOrganization);
        }
      }
    } catch (error) {
      console.error('Failed to fetch orgs', error);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin && isAllowedForSuperAdmin) {
      fetchOrgs('');
    }
    if (isAdmin && user?.id) {
      fetchOrgs(user.id);
    }
  }, [isSuperAdmin, isAllowedForSuperAdmin, isAdmin, user?.id]);

  const handleOrganizationChange = async event => {
    const orgId = event.target.value;
    const sourceOrgs = organizations;
    const organization = sourceOrgs.find(org => org.id === orgId);
    const activeOrganization = sourceOrgs.find(org => org?.deactivate_login !== 1);
    if (organization) {
      if (organization?.deactivate_login === 1 && (!isSuperAdmin || isSuperAdminMasquerading)) {
        const open = useAuthStore.getState().openOrgLoginDisabledModal;
        if (activeOrganization) {
          open({
            action: 'switch_org',
            disabledOrgName: organization?.name || '',
            activeOrganization,
            reloadAfterSwitch: !isSuperAdmin
          });
        } else {
          open({
            action: 'logout',
            orgName: organization?.name || ''
          });
        }
        return;
      }
      const targetOrgs = user?.user_organizations?.filter(
        uo => String(uo.organization_id) === String(orgId) && uo.status !== 'DELETED' && uo.status !== 'INVITED'
      );
      const availableRoles = targetOrgs?.map(uo => uo.user_role) || [];
      if (availableRoles.length > 0 && !availableRoles.includes(user?.user_role)) {
        const fallbackRole = availableRoles.includes('ADMIN') ? 'ADMIN' : (availableRoles.includes('LEADER') ? 'LEADER' : (availableRoles.includes('DISCIPLE') ? 'DISCIPLE' : availableRoles[0]));
        try {
          if (isSuperAdminMasquerading) {
            const res = await superAdminMasqueradeRoleSwitchApi({ role: fallbackRole, orgId: orgId });
            if (res?.data?.status === 200) {
              const { authToken, user: switchedUser } = res.data.data;
              loginNewUser({ ...switchedUser, authToken, isSuperAdminMasquerading: true, is_impersonate: true });
              useAuthStore.getState().setSuperAdminMasquerading(true);
            }
          } else {
            const res = await addUserRoleApi({ permission: fallbackRole, is_role_change: true, orgId: orgId }, { headers: { 'x-suppress-toast': 'true' } });
            if (res?.data?.status === 200) {
              loginNewUser({ ...res?.data?.data, user_role: fallbackRole });
            }
          }
        } catch (e) {
          return;
        }
      }
      
      setOrganization(organization);
      const selectedOrg = org_to_theme?.find(org => org.org_id == orgId);
      // if (selectedOrg?.theme_id) {
      //   setTheme(selectedOrg.theme_id);
      // }
      if (!isSuperAdmin) {
        window.location.reload(); 
      }
    }
  };
  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsDropdownOpen(false);
  };

  const permissionChangeHandler = async permission => {
    setSelectedRole(permission);
    try {
      if (isSuperAdminMasquerading) {
        const roleData = { role: permission, orgId: selectedOrganization?.id };
        const res = await superAdminMasqueradeRoleSwitchApi(roleData);
        if (res?.data?.status === 200) {
          const { authToken, user: switchedUser } = res.data.data;
          // Explicitly pass isSuperAdminMasquerading so it cannot be lost
          loginNewUser({ ...switchedUser, authToken, isSuperAdminMasquerading: true, is_impersonate: true });
          useAuthStore.getState().setSuperAdminMasquerading(true);
          window.location.reload();
        }
      } else {
        const roleData = {
          permission: permission,
          is_role_change: true,
          orgId: selectedOrganization?.id
        };
        const res = await addUserRoleApi(roleData, { headers: { 'x-suppress-toast': 'true' } });
        if (res?.data?.status === 200) {
          loginNewUser({ ...res?.data?.data, user_role: permission });
          window.location.reload();
        }
      }
    } catch (error) { }
  };

  const toggleDropdown = event => {
    event.stopPropagation();
    setIsDropdownOpen(prevState => !prevState);
  };

  const logoutUserHandler = () => {
    logoutUser();
    clearLeaderData();
    clearAdminData();
  };

  const [isExitMasqueradeModalOpen, setIsExitMasqueradeModalOpen] = useState(false);
  const [exitMasqueradeLoading, setExitMasqueradeLoading] = useState(false);

  const exitMasqueradeHandler = () => {
    setIsExitMasqueradeModalOpen(true);
  };

  const executeExitMasquerade = async () => {
    try {
      setExitMasqueradeLoading(true);
      const res = await superAdminExitMasqueradeApi();
      if (res?.data?.status === 200) {
        const { authToken, user: superAdminUser } = res.data.data;
        const returnUrl = localStorage.getItem('masqueradeReturnUrl') || '/super-admin/all-users';
        localStorage.removeItem('masqueradeReturnUrl');

        // Directly update localStorage to avoid React state changes triggering PrivateRoutes redirect
        const authStorageStr = localStorage.getItem('auth-storage');
        if (authStorageStr) {
          try {
            const authStorage = JSON.parse(authStorageStr);
            if (authStorage.state) {
              authStorage.state.user = { ...superAdminUser };
              authStorage.state.token = authToken;
              authStorage.state.preferedTimezone = superAdminUser.prefered_timezone;
              authStorage.state.user_prefered_lang = superAdminUser.user_prefered_lang;
              authStorage.state.is_admin_login_mobile = superAdminUser.is_admin_login_mobile || false;
              authStorage.state.orgLoginDisabledModal = null;
              authStorage.state.isSuperAdminMasquerading = false;
              const preMasqueradeOrgStr = localStorage.getItem('preMasqueradeOrg');
              if (preMasqueradeOrgStr) {
                try {
                  const preMasqueradeOrg = JSON.parse(preMasqueradeOrgStr);
                  if (preMasqueradeOrg) {
                    authStorage.state.selectedOrganization = preMasqueradeOrg;
                  }
                } catch (e) {}
                localStorage.removeItem('preMasqueradeOrg');
              }
              localStorage.setItem('auth-storage', JSON.stringify(authStorage));
            }
          } catch (e) {
            console.error("Failed to parse auth storage", e);
          }
        }

        window.location.href = returnUrl;
      } else if (res?.response?.status === 400 || res?.status === 400) {
        // Interceptor might return the error object directly instead of throwing
        setSuperAdminMasquerading(false);
        const { authToken, user: superAdminUser } = res.response.data || res.data;
        loginNewUser({ ...superAdminUser, authToken });
        window.location.replace('/super-admin/dashboard');
      }
    } catch (error) {
      if (error?.response?.status === 400) {
        setSuperAdminMasquerading(false);
        const { authToken, user: superAdminUser } = error.response.data.data;
        loginNewUser({ ...superAdminUser, authToken });
        window.location.replace('/super-admin/dashboard');
      }
    } finally {
      setExitMasqueradeLoading(false);
      setIsExitMasqueradeModalOpen(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      const res = await deleteUser(user?.id);
      if (res?.data?.status === 200) {
        setModalOpen(false);
        logoutUserHandler();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        flexGrow={1}
        justifyContent="space-between"
        alignItems="center"
        p={2}
        bgcolor="#f9f9f9"
        sx={{
          display: {
            xs: 'flex',
            sm: 'flex',
            md: 'flex',
            lg: 'flex',
            xl: 'flex'
          },
          marginTop: {
            xs: '30px',
            sm: '30px',
            md: 0,
            lg: 0
          }
        }}
      >
        <Box width="60%" display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography
              variant="h4"
              sx={{
                color: '#000 !important',
                fontWeight: 600,
                fontSize: {
                  xs: '18px',
                  sm: '18px',
                  md: '18px'
                }
              }}
            >
              {heading}
            </Typography>
            <Box ml={4}>
              {isAddingUser && (
                <Box
                  sx={{
                    display: { md: 'flex', xs: 'none', sm: 'none' }
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton
                    onClick={handleAddClick}
                    sx={{
                      '&:hover': {
                        borderRadius: '12px',
                        height: '30px'
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Leaders')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingGroup && (
                <Box
                  sx={{
                    display: { md: 'flex', xs: 'none', sm: 'none' }
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton
                    onClick={handleAddClick}
                    sx={{
                      '&:hover': {
                        borderRadius: '12px',
                        height: '30px'
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Group')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingDisciple && (
                <Box
                  sx={{
                    display: { md: 'flex', xs: 'none', sm: 'none' }
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton
                    onClick={handleAddClick}
                    sx={{
                      '&:hover': {
                        borderRadius: '12px',
                        height: '30px'
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Disciple')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingOrganisation && (
                <Box
                  sx={{
                    display: { md: 'flex', xs: 'none', sm: 'none' }
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton onClick={handleAddClick}>
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Organization')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingCampaign && (
                <Box
                  sx={{
                    display: { md: 'flex', xs: 'none', sm: 'none' }
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton onClick={handleAddClick}>
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Campaign')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingDraftDay && (
                <Box
                  sx={{
                    display: { md: 'flex', xs: 'none', sm: 'none' }
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <IconButton onClick={handleAddClick}>
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Draft Disciple')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
            </Box>
            {/* Uncomment it later */}
            {/* <Box ml={4}>
              {isAddingUser && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: {md: 'space-between', xs: 'flex-end'},
                    alignItems: 'center',
                    marginRight: {md: 0, xs: '-15px'},
                    marginLeft: {md: 0, xs: '-90px'},
                    marginTop: {md: 0, xs: '35px'}
                  }}
                >
                  <IconButton
                    onClick={handleAddClick}
                    sx={{
                      '&:hover': {
                        borderRadius: '12px',
                        height: {md: '30px', xs: 'auto'} // Keep height change for desktop only
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Leaders')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingGroup && (
                 <Box
                 sx={{
                   display: 'flex',
                   justifyContent: {md: 'space-between', xs: 'flex-end'},
                   alignItems: 'center',
                   marginRight: {md: 0, xs: '-15px'},
                   marginLeft: {md: 0, xs: '-90px'},
                   marginTop: {md: 0, xs: '35px'}
                 }}
               >
                 <IconButton
                   onClick={handleAddClick}
                   sx={{
                     '&:hover': {
                       borderRadius: '12px',
                       height: {md: '30px', xs: 'auto'}
                     }
                   }}
                 >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Group')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingDisciple && (
                  <Box
                  sx={{
                    display: 'flex',
                    justifyContent: {md: 'space-between', xs: 'flex-end'},
                    alignItems: 'center',
                    marginRight: {md: 0, xs: '-15px'},
                    marginLeft: {md: 0, xs: '-90px'},
                    marginTop: {md: 0, xs: '35px'}
                  }}
                >
                  <IconButton
                    onClick={handleAddClick}
                    sx={{
                      '&:hover': {
                        borderRadius: '12px',
                        height: {md: '30px', xs: 'auto'}
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Disciple')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingOrganisation && (
                  <Box
                  sx={{
                    display: 'flex',
                    justifyContent: {md: 'space-between', xs: 'flex-end'},
                    alignItems: 'center',
                    marginRight: {md: 0, xs: '-15px'},
                    marginLeft: {md: 0, xs: '-90px'},
                    marginTop: {md: 0, xs: '35px'}
                  }}
                >
                  <IconButton
                    onClick={handleAddClick}
                    sx={{
                      '&:hover': {
                        borderRadius: '12px',
                        height: {md: '30px', xs: 'auto'}
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Organization')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
              {isAddingDraftDay && (
                <Box
                sx={{
                  display: 'flex',
                  justifyContent: {md: 'space-between', xs: 'flex-end'},
                  alignItems: 'center',
                  marginRight: {md: 0, xs: '-15px'},
                  marginLeft: {md: 0, xs: '-90px'},
                  marginTop: {md: 0, xs: '35px'}
                }}
              >
                <IconButton
                  onClick={handleAddClick}
                  sx={{
                    '&:hover': {
                      borderRadius: '12px',
                      height: {md: '30px', xs: 'auto'}
                    }
                  }}
                >
                    <AddIcon
                      sx={{
                        color: '#D0373B',
                        fontSize: '20px'
                        // padding: 1,
                      }}
                    />{' '}
                    <Typography variant="h6" fontSize="14.4px" color="black">
                      {translate('Add Draft Disciple')}
                    </Typography>
                  </IconButton>
                </Box>
              )}
            </Box> */}
          </Box>
          {!isAdminMobileRestricted && (<Box display="flex" alignItems="center" justifyContent={'space-between'} px={4} height={'30px'}>
            {(isAdmin || (isSuperAdmin && isAllowedForSuperAdmin)) && (
              <Select
                className="allow-masquerade-input"
                value={selectedOrganization?.id || ''}
                onChange={handleOrganizationChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                IconComponent={ExpandMoreIcon}
                sx={{
                  marginRight: 4,
                  // Uncomment it later
                  // marginRight: {xs: 0, sm: 0, md: 4},
                  // marginTop: {xs: 10, sm: 10, md: 0},
                  // marginLeft: {xs: -18, sm: -18, md: 0},
                  color: 'gray !important',
                  outline: 'none',
                  borderRadius: '30px',
                  display: 'flex',
                  height: '100%',
                  alignItems: 'center',
                  width: '220px',
                  bgcolor: 'white !important',
                  '& .MuiSelect-select': {
                    padding: '8px 8px',
                    color: 'gray !important'
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#D0373B'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white'
                  }
                }}
              >
                {organizations?.map(org => (
                  <MenuItem
                    key={org.id}
                    value={org.id}
                    sx={{
                      color: 'gray !important',
                      textAlign: 'center',
                      backgroundColor: 'white !important',
                      '&:hover': {
                        backgroundColor: '#f9f9f9'
                      }
                    }}
                  >
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>)}
        </Box>
        <Box display="flex" alignItems="center">
          <Box display="flex" alignItems="center" justifyContent={'space-between'} px={4} height={'30px'}>
            {isSearch && (
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: '30px',
                  padding: '0px 20px',
                  display: {
                    md: 'flex',
                    sm: 'none',
                    xs: 'none'
                  },
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '30px'
                }}
              >
                <IconContainer color={'#D0373B'} icon={searchIcon} width={19} height={19} marginRight={8} />
                <input className="allow-masquerade-input" style={{ height: '100%', outline: 'none', border: 'none' }} placeholder={translate('Search Here')} onChange={e => searchHandler(e.target.value)} type="text" name="search" id="search" />
              </Box>
            )}
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center">
            {isSuperAdminMasquerading && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ExitToAppIcon sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }} />}
                onClick={exitMasqueradeHandler}
                sx={{
                  borderColor: '#000',
                  color: '#000',
                  fontSize: { xs: '0.6rem', sm: '0.75rem' },
                  textTransform: 'none',
                  marginRight: { xs: '8px', sm: '15px' },
                  padding: { xs: '0px 6px', sm: '4px 10px' },
                  height: { xs: '24px', sm: '32px' },
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    borderColor: '#000'
                  }
                }}
              >
                {translate('Exit View Mode')}
              </Button>
            )}
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography variant="body1" mr={2} color="#000" fontWeight={500}>
                {user?.first_name || ''} {user?.last_name || ''}
              </Typography>
              <Typography variant="subtitle2" mr={2} color="#000">
                {translate('Administrator')}
              </Typography>
            </Box>
            <Avatar src={user?.avatar} onClick={handleMenuOpen} sx={{ cursor: 'pointer' }} />
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            sx={{
              '& .MuiMenuItem-root': {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                borderRadius: '12px'
              },
              '& .MuiMenu-list': {
                padding: 0
              }
            }}
            PaperProps={{
              sx: {
                maxHeight: 'none',
                height: isDropdownOpen ? '318px' : user?.user_role === 'SUPERADMIN' ? '140px' : user?.user_role === 'ADMIN' ? (userRole?.length > 1 ? '228px' : '182px') : 'fit-content',
                backgroundColor: '#ffffff',
                borderRadius: '15px'
              }
            }}
          >
            <div>
              {user?.user_role === 'ADMIN' && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: '#ffffff'
                        },
                        fontSize: '1.2rem'
                      }}
                      onClick={() => navigate('/admin-dashboard/profile')}
                    >
                      <IconContainer icon={singlePersonIcon} width={20} height={20} marginLeft={'-6px'} marginRight={'10px'} />
                      <ListItemText
                        primary={translate('Profile')}
                        primaryTypographyProps={{
                          sx: {
                            color: '#000000',
                            fontWeight: '600'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: '#ffffff'
                        },
                        fontSize: '1.2rem'
                      }}
                      onClick={() => navigate('/admin-dashboard/change-password')}
                    >
                      <IconContainer icon={changePasswordIcon} width={20} height={20} marginLeft={'-6px'} marginRight={'10px'} />
                      <ListItemText
                        primary={translate('Change Password')}
                        primaryTypographyProps={{
                          sx: {
                            color: '#000000',
                            fontWeight: '600'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>

                  {!isAdminMobileRestricted && (
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{
                          backgroundColor: '#ffffff',
                          color: '#000000',
                          '&:hover': {
                            backgroundColor: '#ffffff'
                          },
                          fontSize: '1.2rem'
                        }}
                        onClick={() => setModalOpen(true)}
                      >
                        <IconContainer icon={deletesIcon} width={20} height={20} marginLeft={'-6px'} marginRight={'10px'} />
                        <ListItemText
                          primary={translate('Delete My Account')}
                          primaryTypographyProps={{
                            sx: {
                              color: '#000000',
                              fontWeight: '600'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )}
                </>
              )}

              {user?.user_role === 'SUPERADMIN' && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: '#ffffff'
                        },
                        fontSize: '1.2rem'
                      }}
                      onClick={() => navigate('/super-admin/profile')}
                    >
                      <IconContainer icon={singlePersonIcon} width={20} height={20} marginLeft={'-6px'} marginRight={'10px'} />
                      <ListItemText
                        primary={translate('Profile')}
                        primaryTypographyProps={{
                          sx: {
                            color: '#000000',
                            fontWeight: '600'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: '#ffffff'
                        },
                        fontSize: '1.2rem'
                      }}
                      onClick={() => navigate('/super-admin/change-password')}
                    >
                      <IconContainer icon={changePasswordIcon} width={20} height={20} marginLeft={'-6px'} marginRight={'10px'} />
                      <ListItemText
                        primary={translate('Change Password')}
                        primaryTypographyProps={{
                          sx: {
                            color: '#000000',
                            fontWeight: '600'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </>
              )}

              {user?.user_role !== 'SUPERADMIN' && userRole?.length > 1 && (
                <ListItem disablePadding sx={{ display: 'flex', minWidth: '100%', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <ListItemButton
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: '#ffffff'
                      },
                      fontSize: '1.2rem',
                      minWidth: '100%'
                    }}
                    onClick={toggleDropdown}
                  >
                    <Box display="flex" alignItems="center">
                      <IconContainer icon={switchAccountIcon} width={20} height={20} marginLeft={'-6px'} marginRight={'10px'} />
                      <ListItemText
                        primary={translate('Switch Accounts')}
                        primaryTypographyProps={{
                          sx: {
                            color: '#000000',
                            fontWeight: '600'
                          }
                        }}
                      />
                    </Box>
                    <ArrowForwardIosRoundedIcon style={{ fontSize: '15px', marginLeft: '3px' }} />
                  </ListItemButton>
                  {isDropdownOpen && (
                    <Box width="100%">
                      {userRole.map(role => (
                        <ListItem
                          key={role.value}
                          onClick={() => {
                            permissionChangeHandler(role.value);
                            setIsDropdownOpen(false);
                          }}
                          sx={{
                            cursor: 'pointer',
                            color: '#000000',
                            maxHeight: '30px',
                            width: '100%',
                            backgroundColor: role.label === selectedRole ? '#D32F2F' : '#ffffff'
                          }}
                        >
                          <ListItemText
                            primary={translate(role.label)}
                            primaryTypographyProps={{
                              sx: {
                                color: role.label === selectedRole ? '#ffffff' : '#000000'
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </Box>
                  )}
                </ListItem>
              )}

              <ListItem disablePadding>
                <ListItemButton
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: '#ffffff'
                    },
                    fontSize: '1.2rem'
                  }}
                  onClick={() => logoutUserHandler()}
                >
                  <IconContainer icon={exitIcon} width={20} height={20} marginLeft={'-4px'} marginRight={'10px'} />
                  <ListItemText
                    primary={translate('Log Out')}
                    primaryTypographyProps={{
                      sx: {
                        color: '#000000',
                        fontWeight: '600'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </div>
          </Menu>
        </Box>
      </Box>
      <Modal
        open={modalOpen}
        setOpen={setModalOpen}
        title={translate('Delete Account')}
        modalbgColor={'#ffffff'}
        modalTextColor={'#000000'}
        modalTitleColor={'#d32f2f'}
        buttons={[
          { name: translate('Close'), onClick: () => setModalOpen(false), buttonBgColor: '#ffffff', buttonTextColor: 'black', buttonHoverColor: '#f5f5f5', type: 'outlined' },
          { name: translate('Delete'), onClick: () => handleDeleteUser(), loading: loading, disabled: isDiscipleView, buttonBgColor: '#d32f2f', buttonTextColor: '#ffffff', buttonHoverColor: '#b71c1c' }
        ]}
      >
        <Typography sx={{ color: 'black' }}>{translate('Are you sure you want to delete your account? All associated data will be removed from the application soon, and you will be logged out immediately.')}</Typography>
      </Modal>
      <Modal
        buttons={[
          { name: translate('Cancel'), onClick: () => setIsExitMasqueradeModalOpen(false) },
          { name: translate('Exit'), onClick: () => executeExitMasquerade(), loading: exitMasqueradeLoading }
        ]}
        modalbgColor="#fff"
        modalTextColor="#000"
        modalTitleColor="#d32f2f"
        open={isExitMasqueradeModalOpen}
        onClose={() => setIsExitMasqueradeModalOpen(false)}
        title={translate('Exit Masquerade Mode')}
      >
        <Box>
          <Typography color="#000" variant="body1" mb={2}>
            {translate('Are you sure you want to exit masquerade mode?')}
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default DashboardHeader;
